import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Panel, Overline, MetricField, EmptyValue, EmptyState, SportBadge, ClassBadge,
  DeltaValue, SourceBadge, TrendChart,
} from "@/components/portal/Bits";
import { COLORS, formatRelative } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  RefreshCw, Pencil, Search, Plus, Trash2, Loader2, Share2, Printer, Link2,
  Megaphone, FileText, Activity, Flame, Instagram, Copy, Check, GraduationCap,
} from "lucide-react";
import { RecruitingTab } from "@/components/portal/RecruitingTab";

function Avatar({ athlete, size = 80 }) {
  const initials = (athlete.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("");
  if (athlete.headshot_url) {
    return <img src={athlete.headshot_url} alt={athlete.name} className="rounded-md object-cover object-top border border-border" style={{ width: size, height: size }} />;
  }
  return (
    <div className="rounded-md bg-secondary border border-border flex items-center justify-center font-display text-2xl text-muted-foreground" style={{ width: size, height: size }}>
      {initials}
    </div>
  );
}

export default function AthleteProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canShare = isAdmin || user?.athlete_id === id;

  const [athlete, setAthlete] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [deltas, setDeltas] = useState(null);
  const [sources, setSources] = useState(null);
  const [pitch, setPitch] = useState(null);
  const [kit, setKit] = useState(null);
  const [retention, setRetention] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(async () => {
    const [a, s, d, src, p, k, r, al] = await Promise.all([
      api.get(`/athletes/${id}`),
      api.get(`/athletes/${id}/snapshots`),
      api.get(`/athletes/${id}/deltas`),
      api.get(`/athletes/${id}/sources`),
      api.get(`/athletes/${id}/pitch`),
      api.get(`/athletes/${id}/media-kit`),
      api.get(`/athletes/${id}/retention`),
      api.get(`/athletes/${id}/alerts`),
    ]);
    setAthlete(a.data); setSnapshots(s.data); setDeltas(d.data); setSources(src.data);
    setPitch(p.data); setKit(k.data); setRetention(r.data); setAlerts(al.data.alerts || []);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const syncEspn = async () => {
    const t = toast.loading("Syncing live ESPN data…");
    try {
      const { data } = await api.post(`/athletes/${id}/sync-espn`);
      toast.dismiss(t);
      if (data.ok) toast.success(`ESPN synced · ${data.stats_count} stat lines`);
      else toast.error(data.error || "No ESPN data found");
      load();
    } catch (err) {
      toast.dismiss(t);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!athlete) return <div className="text-muted-foreground">Loading profile…</div>;

  return (
    <div>
      {/* Header */}
      <Panel className="p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <Avatar athlete={athlete} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <SportBadge sport={athlete.sport} />
              <ClassBadge classification={athlete.classification} />
            </div>
            <h1 className="font-display-tight text-4xl sm:text-5xl text-white">{athlete.name}</h1>
            {athlete.alias && athlete.alias !== athlete.name && (
              <p className="text-sm text-muted-foreground/70 mt-0.5" data-testid="athlete-alias">aka {athlete.alias}</p>
            )}
            <p className="text-muted-foreground mt-1">{athlete.school}{athlete.position ? ` · ${athlete.position}` : ""}</p>
            {athlete.commitment && (
              <p className="text-sm text-[#E1FF00] mt-1" data-testid="athlete-commitment">Committed · {athlete.commitment}</p>
            )}
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2" data-testid="admin-actions">
              <EditDialog athlete={athlete} onSaved={load} />
              <EspnSearchDialog athlete={athlete} onSaved={load} />
              <Button variant="outline" className="border-border gap-2" onClick={syncEspn} data-testid="sync-espn-button">
                <RefreshCw className="h-4 w-4" /> Sync ESPN
              </Button>
              <AddSnapshotDialog id={id} onSaved={load} />
              <DeleteAthleteDialog athlete={athlete} onDeleted={() => navigate("/roster")} />
            </div>
          )}
        </div>
      </Panel>

      <Tabs defaultValue="overview">
        <TabsList className="bg-secondary border border-border flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview"><FileText className="h-4 w-4 mr-1.5" />Overview</TabsTrigger>
          <TabsTrigger value="pitch" data-testid="tab-pitch"><Megaphone className="h-4 w-4 mr-1.5" />Pitch</TabsTrigger>
          <TabsTrigger value="mediakit" data-testid="tab-mediakit"><Share2 className="h-4 w-4 mr-1.5" />Media Kit</TabsTrigger>
          <TabsTrigger value="retention" data-testid="tab-retention"><Activity className="h-4 w-4 mr-1.5" />Retention</TabsTrigger>
          <TabsTrigger value="recruiting" data-testid="tab-recruiting"><GraduationCap className="h-4 w-4 mr-1.5" />Recruiting</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts"><Flame className="h-4 w-4 mr-1.5" />Alerts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab athlete={athlete} deltas={deltas} sources={sources} snapshots={snapshots} />
        </TabsContent>
        <TabsContent value="pitch" className="mt-6"><PitchTab pitch={pitch} athlete={athlete} /></TabsContent>
        <TabsContent value="mediakit" className="mt-6"><MediaKitTab kit={kit} canShare={canShare} id={id} onChanged={load} /></TabsContent>
        <TabsContent value="retention" className="mt-6"><RetentionTab retention={retention} /></TabsContent>
        <TabsContent value="recruiting" className="mt-6"><RecruitingTab athlete={athlete} isAdmin={isAdmin} onSaved={load} /></TabsContent>
        <TabsContent value="alerts" className="mt-6"><AlertsTab alerts={alerts} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Overview ---------------- */
function OverviewTab({ athlete, deltas, sources, snapshots }) {
  const stats = athlete.current_stats || [];
  const socialSeries = snapshots.filter((s) => s.social && Object.keys(s.social).length)
    .map((s) => ({ week: s.week, followers: (s.social.instagram_followers || 0) + (s.social.tiktok_followers || 0) || null, reach: s.social.reach ?? null }));

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Panel className="p-6">
          <Overline className="mb-2">Bio & identifiers</Overline>
          <div className="grid sm:grid-cols-2 gap-x-8">
            <MetricField label="Height" value={athlete.height} source={athlete.stats_source} updatedAt={athlete.stats_updated_at} testId="field-height" />
            <MetricField label="Weight" value={athlete.weight} source={athlete.stats_source} updatedAt={athlete.stats_updated_at} testId="field-weight" />
            <MetricField label="Classification" value={athlete.classification} empty="Unverified" testId="field-classification" />
            <MetricField label="Position" value={athlete.position} testId="field-position" />
            <MetricField label="PFF Score" value={athlete.pff_score} empty="Not verified (license pending)" source={athlete.pff_source} updatedAt={athlete.pff_updated_at} testId="field-pff" />
            <MetricField label="ESPN ID" value={athlete.espn_id} href={athlete.espn_link} testId="field-espn" />
            <MetricField label="Instagram" value={athlete.instagram_handle ? `@${athlete.instagram_handle}` : null} href={athlete.instagram_link} empty="No data yet" testId="field-instagram" />
            <MetricField label="Last ESPN sync" value={athlete.espn_last_synced ? formatRelative(athlete.espn_last_synced) : null} empty="Never synced" testId="field-espn-sync" />
          </div>
        </Panel>

        <Panel className="p-6">
          <div className="flex items-center justify-between mb-3">
            <Overline>Current statistics</Overline>
            {athlete.stats_source && <SourceBadge source={athlete.stats_source} updatedAt={athlete.stats_updated_at} testId="stats-source-badge" />}
          </div>
          {stats.length === 0 ? (
            <EmptyState testId="stats-empty" title="No data yet"
              hint="Live ESPN statistics appear here after an admin maps an ESPN ID and runs Sync ESPN." />
          ) : (
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2" data-testid="stats-grid">
              {stats.map((s, i) => (
                <div key={i} className="flex items-center justify-between border-b border-border/50 py-2">
                  <span className="text-sm text-muted-foreground">{s.label}</span>
                  <span className="text-sm font-semibold text-white">{s.value}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel className="p-6">
          <Overline className="mb-3">Week-over-week</Overline>
          <DeltaBlocks deltas={deltas} />
          {socialSeries.length >= 2 && (
            <div className="mt-6">
              <Overline className="mb-2">Followers trend</Overline>
              <TrendChart data={socialSeries} dataKey="followers" />
            </div>
          )}
        </Panel>
      </div>

      <div className="space-y-6">
        <Panel className="p-6">
          <Overline className="mb-4">Data sources</Overline>
          {sources && <SourceStatus sources={sources} />}
        </Panel>
        {athlete.case_study && (
          <Panel className="p-6 border-[#E1FF00]/20">
            <Overline className="mb-2 text-[#E1FF00]">Public case study</Overline>
            <p className="text-xs text-muted-foreground/70 mb-2">Not a current metric.</p>
            <p className="text-sm text-white/90 leading-relaxed" data-testid="case-study">{athlete.case_study}</p>
          </Panel>
        )}
      </div>
    </div>
  );
}

function DeltaBlocks({ deltas }) {
  if (!deltas) return null;
  const groups = [["athletic", "Athletic"], ["social", "Social"]];
  const total = Object.keys(deltas.athletic || {}).length + Object.keys(deltas.social || {}).length;
  if (total === 0) {
    return <EmptyState testId="deltas-empty" title="No week-over-week history yet"
      hint="Weekly snapshots build this over time. Deltas compute automatically once at least two snapshots exist." />;
  }
  return (
    <div className="space-y-5" data-testid="deltas">
      {deltas.latest_week && <p className="text-xs text-muted-foreground">Comparing {deltas.previous_week || "—"} → {deltas.latest_week}</p>}
      {groups.map(([key, label]) => {
        const entries = Object.entries(deltas[key] || {});
        if (entries.length === 0) return null;
        return (
          <div key={key}>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{label}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {entries.map(([metric, v]) => (
                <div key={metric} className="rounded-md border border-border px-3 py-2 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground truncate">{metric}</div>
                    <div className="text-white font-semibold">{v.current}</div>
                  </div>
                  <DeltaValue delta={v.delta} direction={v.direction} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SourceStatus({ sources }) {
  const Row = ({ title, ok, sub }) => (
    <div className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0">
      <span className={`mt-1.5 h-2 w-2 rounded-full ${ok ? "bg-[#E1FF00]" : "bg-muted-foreground/40"}`} />
      <div>
        <div className="text-sm font-medium text-white">{title}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </div>
    </div>
  );
  return (
    <div data-testid="source-status">
      <Row title="ESPN (public)" ok={sources.espn.mapped}
        sub={sources.espn.mapped ? `Mapped · ${sources.espn.last_synced ? formatRelative(sources.espn.last_synced) : "not synced"}` : "No ESPN ID mapped yet"} />
      <Row title="PFF (licensed)" ok={sources.pff.configured} sub={sources.pff.note} />
      <Row title="Instagram (authorized)" ok={sources.instagram.mapped && sources.instagram.configured} sub={sources.instagram.note} />
    </div>
  );
}

/* ---------------- Pitch ---------------- */
function PitchTab({ pitch, athlete }) {
  if (!pitch) return null;
  const g = pitch.verified_growth;
  return (
    <div className="space-y-6">
      <Panel className="p-8 bg-gradient-to-b from-[#0d0d0d] to-[#080808]">
        <Overline className="text-[#E1FF00]">The pitch</Overline>
        <h2 className="font-display-tight text-4xl sm:text-5xl text-white mt-2">{athlete.name}</h2>
        <p className="text-muted-foreground mt-1">{athlete.sport?.toUpperCase()} · {athlete.school}</p>
      </Panel>

      <Panel className="p-6">
        <Overline className="mb-3">Verified growth</Overline>
        {g?.has_data ? (
          <div className="grid sm:grid-cols-3 gap-4" data-testid="pitch-growth">
            <GrowthStat label="Total following" value={g.latest?.followers} delta={g.followers_growth_pct} suffix="%" />
            <GrowthStat label="Engagement rate" value={g.engagement_rate != null ? `${g.engagement_rate}%` : null} delta={g.engagement_rate_delta} suffix=" pts" />
            <GrowthStat label="Weekly reach" value={g.latest?.reach} delta={g.reach_delta} />
          </div>
        ) : (
          <EmptyState testId="pitch-no-growth" title="No verified growth yet"
            hint="Verified week-over-week growth appears once authorized social snapshots are recorded. Nothing is estimated." />
        )}
      </Panel>

      <Panel className="p-6">
        <Overline className="mb-3">On-field highlights</Overline>
        {pitch.highlights?.length ? (
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2" data-testid="pitch-highlights">
            {pitch.highlights.map((s, i) => (
              <div key={i} className="flex items-center justify-between border-b border-border/50 py-2">
                <span className="text-sm text-muted-foreground">{s.label}</span>
                <span className="text-sm font-semibold text-white">{s.value}</span>
              </div>
            ))}
            {pitch.stats_source && <div className="pt-2"><SourceBadge source={pitch.stats_source} updatedAt={pitch.stats_updated_at} /></div>}
          </div>
        ) : (
          <EmptyState title="No verified stats yet" hint="Sync ESPN to populate real, sourced statistics." />
        )}
      </Panel>

      {pitch.case_study && (
        <Panel className="p-6 border-[#E1FF00]/20">
          <Overline className="mb-2 text-[#E1FF00]">Public case study</Overline>
          <p className="text-xs text-muted-foreground/70 mb-2">Historical context — not a current metric.</p>
          <p className="text-sm text-white/90 leading-relaxed">{pitch.case_study}</p>
        </Panel>
      )}
    </div>
  );
}

function GrowthStat({ label, value, delta, suffix }) {
  return (
    <div className="rounded-md border border-border p-4">
      <Overline>{label}</Overline>
      <div className="font-display text-3xl text-white mt-2">{value ?? <EmptyValue label="No data yet" />}</div>
      {value != null && <div className="mt-1"><DeltaValue delta={delta} direction={delta > 0 ? "up" : delta < 0 ? "down" : "flat"} suffix={suffix} /></div>}
    </div>
  );
}

/* ---------------- Media Kit ---------------- */
function MediaKitTab({ kit, canShare, id, onChanged }) {
  const [approved, setApproved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!kit) return null;

  const share = async () => {
    setBusy(true);
    try {
      await api.post(`/athletes/${id}/media-kit/share`, { approved: true });
      toast.success("Media kit is now shareable");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const unshare = async () => {
    setBusy(true);
    try { await api.post(`/athletes/${id}/media-kit/unshare`); toast.success("Sharing stopped"); onChanged(); }
    finally { setBusy(false); }
  };
  const publicUrl = kit.share_token ? `${window.location.origin}/kit/${kit.share_token}` : null;
  const copy = () => { navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Public link copied"); };

  return (
    <div className="space-y-6">
      <Panel className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Overline>Brand-deal media kit</Overline>
            <p className="text-sm text-muted-foreground mt-1">Living kit built from verified week-over-week reach & engagement.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-border gap-2" onClick={() => window.print()} data-testid="kit-export-button">
              <Printer className="h-4 w-4" /> Export / Print
            </Button>
          </div>
        </div>
      </Panel>

      <div className="grid sm:grid-cols-3 gap-4">
        <KitStat label="Total following" value={kit.followers?.total} delta={kit.followers?.growth_pct} suffix="%" source={kit.followers?.source} />
        <KitStat label="Weekly reach" value={kit.reach?.current} delta={kit.reach?.delta} source={kit.reach?.source} />
        <KitStat label="Engagement rate" value={kit.engagement?.rate != null ? `${kit.engagement.rate}%` : null} delta={kit.engagement?.delta} suffix=" pts" source={kit.engagement?.source} />
      </div>

      {kit.weekly?.length >= 2 ? (
        <Panel className="p-6">
          <Overline className="mb-3">Reach & engagement over time</Overline>
          <TrendChart data={kit.weekly.map((w) => ({ week: w.week, reach: w.reach, engagement_rate: w.engagement_rate }))} dataKey="reach" color={COLORS.neutral} />
        </Panel>
      ) : (
        <EmptyState testId="kit-no-trend" title="No week-over-week reach yet"
          hint="Reach and engagement trends populate from authorized social snapshots — never estimated." />
      )}

      {canShare && (
        <Panel className="p-6" data-testid="kit-share-panel">
          <Overline className="mb-3">Sharing</Overline>
          {kit.shared ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-[#E1FF00]"><Link2 className="h-4 w-4" /> Public sharing is ON</div>
              {publicUrl && (
                <div className="flex items-center gap-2">
                  <Input readOnly value={publicUrl} className="bg-secondary border-border text-xs" data-testid="kit-public-url" />
                  <Button size="icon" variant="outline" className="border-border shrink-0" onClick={copy}>
                    {copied ? <Check className="h-4 w-4 text-[#E1FF00]" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              )}
              <Button variant="outline" className="border-border text-[#FF6666] hover:text-[#FF3333]" onClick={unshare} disabled={busy} data-testid="kit-unshare-button">
                Stop sharing
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Media kits are private by default. Public sharing requires explicit audience approval.</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={approved} onChange={(e) => setApproved(e.target.checked)} className="mt-1 accent-[#E1FF00]" data-testid="kit-approve-checkbox" />
                <span className="text-sm text-white">I confirm the athlete/audience has explicitly approved sharing this media kit publicly.</span>
              </label>
              <Button className="bg-white text-black hover:bg-white/90 font-semibold gap-2" disabled={!approved || busy} onClick={share} data-testid="kit-share-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Generate public link
              </Button>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}

function KitStat({ label, value, delta, suffix, source }) {
  return (
    <Panel className="p-5">
      <Overline>{label}</Overline>
      <div className="font-display text-3xl text-white mt-2">{value ?? <EmptyValue label="No data yet" />}</div>
      {value != null && delta != null && <div className="mt-1"><DeltaValue delta={delta} direction={delta > 0 ? "up" : delta < 0 ? "down" : "flat"} suffix={suffix} /></div>}
      {value != null && source && <div className="mt-2"><SourceBadge source={source} /></div>}
    </Panel>
  );
}

/* ---------------- Retention ---------------- */
function RetentionTab({ retention }) {
  if (!retention) return null;
  const ss = retention.social || {};
  if (!ss.has_data) {
    return <EmptyState testId="retention-empty" title="No retention data yet"
      hint="Your week-over-week growth chart builds as authorized social snapshots are recorded. No numbers are invented." />;
  }
  const series = (ss.series || []).map((w) => ({ week: w.week, followers: w.followers, engagement_rate: w.engagement_rate }));
  return (
    <div className="space-y-6" data-testid="retention-content">
      <div className="grid sm:grid-cols-3 gap-4">
        <KitStat label="Following" value={ss.latest?.followers} delta={ss.followers_growth_pct} suffix="%" source={ss.latest?.source} />
        <KitStat label="Engagement rate" value={ss.engagement_rate != null ? `${ss.engagement_rate}%` : null} delta={ss.engagement_rate_delta} suffix=" pts" source={ss.latest?.source} />
        <KitStat label="Weekly reach" value={ss.latest?.reach} delta={ss.reach_delta} source={ss.latest?.source} />
      </div>
      {series.length >= 2 && (
        <Panel className="p-6">
          <Overline className="mb-3">Following growth</Overline>
          <TrendChart data={series} dataKey="followers" />
        </Panel>
      )}
    </div>
  );
}

/* ---------------- Alerts ---------------- */
function AlertsTab({ alerts }) {
  if (!alerts || alerts.length === 0) {
    return <EmptyState testId="athlete-alerts-empty" icon={Flame} title="No alerts yet"
      hint="Engagement spikes, follower surges and on-field movement surface here from verified snapshots. PFF movement needs a licensed feed." />;
  }
  return (
    <div className="space-y-3" data-testid="athlete-alerts">
      {alerts.map((a, i) => (
        <Panel key={i} className="p-5 flex items-start gap-4">
          <div className="rounded-md bg-[#E1FF00]/10 border border-[#E1FF00]/30 p-2"><Flame className="h-5 w-5 text-[#E1FF00]" /></div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-white">{a.title} <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-2">{a.week}</span></div>
            <div className="text-sm text-muted-foreground">{a.detail}</div>
            <div className="mt-1"><SourceBadge source={a.source} /></div>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-[#E1FF00]">{a.severity}</span>
        </Panel>
      ))}
    </div>
  );
}

/* ---------------- Admin dialogs ---------------- */
function DeleteAthleteDialog({ athlete, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/athletes/${athlete.id}`);
      toast.success(`${athlete.name} removed from the roster`);
      onDeleted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive gap-2" data-testid="delete-athlete-button">
          <Trash2 className="h-4 w-4" /> Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-card border-border">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">Delete {athlete.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes {athlete.name} and their recorded snapshots from the roster.
            This cannot be undone. Users and other athletes are not affected.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-athlete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={remove} disabled={busy}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-athlete-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete athlete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditDialog({ athlete, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({
      classification: athlete.classification || "", position: athlete.position || "",
      height: athlete.height || "", weight: athlete.weight || "",
      espn_id: athlete.espn_id || "", instagram_handle: athlete.instagram_handle || "",
      pff_score: athlete.pff_score ?? "", case_study: athlete.case_study || "",
      commitment: athlete.commitment || "",
    });
  }, [open, athlete]);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      body.pff_score = body.pff_score === "" ? null : parseFloat(body.pff_score);
      Object.keys(body).forEach((k) => { if (body[k] === "") body[k] = null; });
      await api.put(`/athletes/${athlete.id}`, body);
      toast.success("Profile updated");
      setOpen(false); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-border gap-2" data-testid="edit-athlete-button"><Pencil className="h-4 w-4" /> Edit</Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Edit {athlete.name}</DialogTitle>
          <DialogDescription>Only confirmed roster values are fixed. Fill verified data here — leave blank to keep it "Not verified".</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          {[["classification", "Classification"], ["position", "Position"], ["height", "Height"], ["weight", "Weight"], ["commitment", "Commitment (college)"], ["espn_id", "ESPN ID"], ["instagram_handle", "Instagram handle"]].map(([k, l]) => (
            <div key={k} className="space-y-2"><Label>{l}</Label>
              <Input value={form[k] ?? ""} onChange={set(k)} className="bg-secondary border-border" data-testid={`edit-${k}`} /></div>
          ))}
          <div className="space-y-2">
            <Label>PFF Score</Label>
            <Input value={form.pff_score ?? ""} onChange={set("pff_score")} placeholder="Leave blank (license pending)" className="bg-secondary border-border" data-testid="edit-pff_score" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Public case study (optional)</Label>
          <Textarea value={form.case_study ?? ""} onChange={set("case_study")} rows={3} className="bg-secondary border-border" data-testid="edit-case_study" />
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-white text-black hover:bg-white/90 font-semibold" data-testid="edit-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EspnSearchDialog({ athlete, onSaved }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(athlete.name);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/espn/search`, { params: { sport: athlete.sport, q } }); setResults(data.results || []); }
    finally { setLoading(false); }
  };
  const use = async (rid) => {
    await api.put(`/athletes/${athlete.id}`, { espn_id: rid });
    toast.success("ESPN ID mapped. Run Sync ESPN to pull stats.");
    setOpen(false); onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setResults([]); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-border gap-2" data-testid="espn-search-button"><Search className="h-4 w-4" /> Find ESPN ID</Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border">
        <DialogHeader><DialogTitle className="font-display text-2xl">Find ESPN athlete</DialogTitle>
          <DialogDescription>Search ESPN's public directory and map the correct athlete ID.</DialogDescription></DialogHeader>
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} className="bg-secondary border-border" data-testid="espn-search-input" />
          <Button onClick={run} disabled={loading} className="bg-white text-black hover:bg-white/90" data-testid="espn-search-run">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}</Button>
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="espn-search-results">
          {results.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No results yet. Try a search.</p> :
            results.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                <div className="flex items-center gap-3 min-w-0">
                  {r.headshot && <img src={r.headshot} alt="" className="h-8 w-8 rounded object-cover" />}
                  <div className="min-w-0"><div className="text-sm text-white truncate">{r.name}</div><div className="text-xs text-muted-foreground truncate">{r.detail} · ID {r.id}</div></div>
                </div>
                <Button size="sm" variant="outline" className="border-border shrink-0" onClick={() => use(r.id)} data-testid={`espn-use-${r.id}`}>Use</Button>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddSnapshotDialog({ id, onSaved }) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("Manual");
  const [social, setSocial] = useState({ instagram_followers: "", tiktok_followers: "", reach: "", engagement: "", engagement_rate: "" });
  const [rows, setRows] = useState([{ label: "", value: "" }]);
  const [saving, setSaving] = useState(false);
  const setSoc = (k) => (e) => setSocial((p) => ({ ...p, [k]: e.target.value }));
  const num = (v) => (v === "" || v == null ? undefined : Number(v));
  const save = async () => {
    setSaving(true);
    try {
      const socialObj = {};
      Object.entries(social).forEach(([k, v]) => { if (v !== "") socialObj[k] = num(v); });
      const athleticObj = {};
      rows.forEach((r) => { if (r.label && r.value !== "") athleticObj[r.label] = num(r.value) ?? r.value; });
      await api.post(`/athletes/${id}/snapshots`, { source, social: socialObj, athletic: athleticObj });
      toast.success("Snapshot recorded");
      setOpen(false); setSocial({ instagram_followers: "", tiktok_followers: "", reach: "", engagement: "", engagement_rate: "" }); setRows([{ label: "", value: "" }]);
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-white text-black hover:bg-white/90 font-semibold gap-2" data-testid="add-snapshot-button"><Plus className="h-4 w-4" /> Snapshot</Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Record weekly snapshot</DialogTitle>
          <DialogDescription>Enter only verified numbers from authorized sources. These build week-over-week history and deltas.</DialogDescription></DialogHeader>
        <div className="space-y-2"><Label>Source</Label>
          <Input value={source} onChange={(e) => setSource(e.target.value)} className="bg-secondary border-border" data-testid="snapshot-source" /></div>
        <Overline className="mt-2">Social <Instagram className="inline h-3 w-3" /></Overline>
        <div className="grid grid-cols-2 gap-3">
          {[["instagram_followers", "IG followers"], ["tiktok_followers", "TikTok followers"], ["reach", "Weekly reach"], ["engagement", "Weekly engagement"], ["engagement_rate", "Engagement rate %"]].map(([k, l]) => (
            <div key={k} className="space-y-2"><Label className="text-xs">{l}</Label>
              <Input value={social[k]} onChange={setSoc(k)} inputMode="decimal" className="bg-secondary border-border" data-testid={`snapshot-${k}`} /></div>
          ))}
        </div>
        <Overline className="mt-2">Athletic (custom)</Overline>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="Metric label" value={r.label} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="bg-secondary border-border" />
              <Input placeholder="Value" value={r.value} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} className="bg-secondary border-border w-32" />
              <Button size="icon" variant="ghost" onClick={() => setRows((p) => p.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="border-border" onClick={() => setRows((p) => [...p, { label: "", value: "" }])}>Add metric</Button>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-white text-black hover:bg-white/90 font-semibold" data-testid="snapshot-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save snapshot"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
